import React, { useState, useMemo, useRef, useCallback } from 'react';
import { LineChart, Line, XAxis, YAxis, CartesianGrid, Tooltip as RechartsTooltip, Legend, ResponsiveContainer, AreaChart, Area, BarChart, Bar, ReferenceLine } from 'recharts';
import Modal from '../common/Modal';
import Button from '../common/Button';
import { Download, FileText, Table, CheckCircle, TrendingUp, Activity, Clock, Crosshair, Box } from 'lucide-react';
import { useSimulation } from '../../hooks/useSimulation';

const ResultsModal = ({ isOpen, onClose }) => {
   const { state, results, downloadResults } = useSimulation();
   const [activeTab, setActiveTab] = useState('primary');
   const chartRef = useRef(null);

   const stats = results?.stats;
   const config = results?.config;
   const events = results?.events || [];

   // ============================================ 
   // 1. DETERMINE EXPERIMENT TYPE
   // ============================================ 
   const experimentType = useMemo(() => {
      if (!results) return 'mcs';

      const p = config?.particle;
      const physics = state.physics; // Access directly from state if needed, or assume encoded in results
      
      // Heuristic detection based on results data presence
      if (stats?.stoppedParticles > stats?.total * 0.1) return 'medical'; // >10% stopped -> Medical/Calorimetry
      if (results.decayTimes?.some(v => v > 0)) return 'decay'; // Decay events present
      if (results.tofSpectrum?.some(v => v > 0) && config?.detectorResponse?.enabled) return 'pid'; // TOF data present
      return 'mcs'; // Default to MCS
   }, [stats, results, config, state.physics]);

   // ============================================ 
   // 2. PREPARE CHART DATA
   // ============================================ 
   
   // MCS Data (Scattering)
   const scatteringData = useMemo(() => {
      if (!results) return [];
      return results.histogram?.bins?.map((count, i) => ({
         angle: i,
         count: count,
         materialCount: results.histogram.materialBins?.[i] || count,
         insertCount: results.histogram.insertBins?.[i] || 0,
      })).filter(d => d.count > 0 || d.angle < 30) || [];
   }, [results]);

   // Medical Data (Depth Dose)
   const depthDoseData = useMemo(() => {
      if (!results || experimentType !== 'medical') return [];
      return results.depthDose?.map((count, i) => ({
         depth: (i / 100) * (config?.phantomThickness * 1.5), // Scale to 1.5x phantom thickness
         dose: count
      })).filter((_, i) => i % 2 === 0) || []; // Downsample for smoother chart
   }, [results, config, experimentType]);

   // Decay Data
   const decayData = useMemo(() => {
      if (!results || experimentType !== 'decay') return [];
      return results.decayTimes?.map((count, i) => ({
         time: i, // Arbitrary time units for now (ns bins)
         count: count
      })) || [];
   }, [results, experimentType]);

   // PID Data (TOF)
   const tofData = useMemo(() => {
      if (!results || experimentType !== 'pid') return [];
      return results.tofSpectrum?.map((count, i) => ({
         bin: i,
         count: count
      })).filter(d => d.count > 0) || [];
   }, [results, experimentType]);

   // Early return if no results - NOW AFTER HOOKS
   if (!results) return null;

   // ============================================ 
   // 3. RENDER CHART CONTENT
   // ============================================ 
   const renderChart = () => {
      switch (experimentType) {
         case 'medical':
            return (
               <div className="h-[350px] w-full bg-white p-2 rounded">
                  <h4 className="text-center font-semibold text-slate-700 mb-2">Bragg Peak (Depth Dose Profile)</h4>
                  <ResponsiveContainer width="100%" height="100%">
                     <AreaChart data={depthDoseData} margin={{ top: 20, right: 30, left: 20, bottom: 40 }}>
                        <CartesianGrid strokeDasharray="3 3" />
                        <XAxis dataKey="depth" label={{ value: 'Depth (cm)', position: 'bottom', offset: 0 }} tickFormatter={v => v.toFixed(1)} />
                        <YAxis label={{ value: 'Rel. Dose', angle: -90, position: 'insideLeft' }} />
                        <RechartsTooltip formatter={(value) => [value, 'Stopped Protons']} labelFormatter={v => `${Number(v).toFixed(2)} cm`} />
                        <Area type="monotone" dataKey="dose" stroke="#E74C3C" fill="#E74C3C" fillOpacity={0.6} />
                        <ReferenceLine x={config?.phantomThickness} stroke="red" strokeDasharray="3 3" label="Phantom End" />
                     </AreaChart>
                  </ResponsiveContainer>
               </div>
            );
         case 'decay':
            return (
               <div className="h-[350px] w-full bg-white p-2 rounded">
                  <h4 className="text-center font-semibold text-slate-700 mb-2">Particle Decay Time Distribution</h4>
                  <ResponsiveContainer width="100%" height="100%">
                     <BarChart data={decayData} margin={{ top: 20, right: 30, left: 20, bottom: 40 }}>
                        <CartesianGrid strokeDasharray="3 3" />
                        <XAxis dataKey="time" label={{ value: 'Decay Time (arb. units)', position: 'bottom', offset: 0 }} />
                        <YAxis label={{ value: 'Events', angle: -90, position: 'insideLeft' }} />
                        <RechartsTooltip />
                        <Bar dataKey="count" fill="#8E44AD" />
                     </BarChart>
                  </ResponsiveContainer>
               </div>
            );
         case 'pid':
            return (
               <div className="h-[350px] w-full bg-white p-2 rounded">
                  <h4 className="text-center font-semibold text-slate-700 mb-2">Time-of-Flight Spectrum</h4>
                  <ResponsiveContainer width="100%" height="100%">
                     <BarChart data={tofData} margin={{ top: 20, right: 30, left: 20, bottom: 40 }}>
                        <CartesianGrid strokeDasharray="3 3" />
                        <XAxis dataKey="bin" label={{ value: 'TOF Bin', position: 'bottom', offset: 0 }} />
                        <YAxis label={{ value: 'Counts', angle: -90, position: 'insideLeft' }} />
                        <RechartsTooltip />
                        <Bar dataKey="count" fill="#2ECC71" />
                     </BarChart>
                  </ResponsiveContainer>
               </div>
            );
         default: // MCS
            return (
               <div className="h-[350px] w-full bg-white p-2 rounded">
                  <h4 className="text-center font-semibold text-slate-700 mb-2">Scattering Angle Distribution (MCS)</h4>
                  <ResponsiveContainer width="100%" height="100%">
                     <BarChart data={scatteringData} margin={{ top: 20, right: 30, left: 20, bottom: 40 }}>
                        <CartesianGrid strokeDasharray="3 3" />
                        <XAxis dataKey="angle" label={{ value: 'Angle (mrad)', position: 'bottom', offset: 0 }} />
                        <YAxis label={{ value: 'Counts', angle: -90, position: 'insideLeft' }} />
                        <RechartsTooltip />
                        <Bar dataKey="materialCount" fill="#3498DB" stackId="a" name="Material" />
                        <Bar dataKey="insertCount" fill="#10B981" stackId="a" name="Insert" />
                     </BarChart>
                  </ResponsiveContainer>
               </div>
            );
      }
   };

   // ============================================ 
   // 4. GENERATE TABS & LABELS
   // ============================================ 
   const getTabs = () => {
      const common = [
         { id: 'data', label: 'Raw Data', icon: Table },
         { id: 'notes', label: 'Analysis Notes', icon: FileText },
      ];
      
      let primary = { id: 'primary', label: 'Overview', icon: TrendingUp };
      
      switch (experimentType) {
         case 'medical': primary = { id: 'primary', label: 'Depth Dose', icon: Activity }; break;
         case 'decay': primary = { id: 'primary', label: 'Decay Curve', icon: Clock }; break;
         case 'pid': primary = { id: 'primary', label: 'TOF Spectrum', icon: Crosshair }; break;
         default: primary = { id: 'primary', label: 'Scattering', icon: Box }; break;
      }

      return [primary, ...common];
   };

   // ============================================ 
   // 5. RENDER
   // ============================================ 
   return (
      <Modal isOpen={isOpen} onClose={onClose} title={`Simulation Results: ${experimentType.toUpperCase()}`} maxWidth="max-w-5xl">
         {/* Tabs */}
         <div className="flex border-b border-slate-200 mb-6">
            {getTabs().map(tab => {
               const Icon = tab.icon;
               return (
                  <button
                     key={tab.id}
                     onClick={() => setActiveTab(tab.id)}
                     className={`px-4 py-3 font-medium text-sm flex items-center gap-2 relative ${activeTab === tab.id ? 'text-blue-600' : 'text-slate-500 hover:text-slate-700'}`}
                  >
                     <Icon size={16} /> {tab.label}
                     {activeTab === tab.id && <div className="absolute bottom-0 left-0 right-0 h-0.5 bg-blue-600" />}
                  </button>
               );
            })}
         </div>

         {/* Content */}
         <div className="min-h-[400px]">
            {activeTab === 'primary' && (
               <div className="space-y-6">
                  {renderChart()}
                  
                  {/* Context-Aware Stats Grid */}
                  <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                     <div className="bg-slate-50 p-4 rounded-lg">
                        <div className="text-xs text-slate-500 uppercase tracking-wider">Total Particles</div>
                        <div className="text-2xl font-bold font-mono text-slate-800">{stats?.total?.toLocaleString()}</div>
                     </div>
                     
                     {experimentType === 'medical' && (
                        <>
                           <div className="bg-red-50 p-4 rounded-lg">
                              <div className="text-xs text-red-600 uppercase tracking-wider">Stopped Particles</div>
                              <div className="text-2xl font-bold font-mono text-red-700">{stats?.stoppedParticles?.toLocaleString()}</div>
                           </div>
                           <div className="bg-slate-50 p-4 rounded-lg">
                              <div className="text-xs text-slate-500 uppercase tracking-wider">Peak Depth</div>
                              <div className="text-2xl font-bold font-mono text-blue-600">{stats?.peakDepth?.toFixed(2) || '-'} cm</div>
                           </div>
                        </>
                     )}

                     {experimentType === 'mcs' && (
                        <>
                           <div className="bg-blue-50 p-4 rounded-lg">
                              <div className="text-xs text-blue-600 uppercase tracking-wider">θ_RMS (Plane)</div>
                              <div className="text-2xl font-bold font-mono text-blue-700">{stats?.rmsScatteringAngle?.toFixed(2)} mrad</div>
                           </div>
                           <div className="bg-green-50 p-4 rounded-lg">
                              <div className="text-xs text-green-600 uppercase tracking-wider">Detection Rate</div>
                              <div className="text-2xl font-bold font-mono text-green-700">{((stats?.detected/stats?.total)*100).toFixed(1)}%</div>
                           </div>
                        </>
                     )}

                     {experimentType === 'decay' && (
                        <>
                           <div className="bg-purple-50 p-4 rounded-lg">
                              <div className="text-xs text-purple-600 uppercase tracking-wider">Decay Events</div>
                              <div className="text-2xl font-bold font-mono text-purple-700">{(stats?.total - stats?.detected)?.toLocaleString()}</div>
                           </div>
                           <div className="bg-slate-50 p-4 rounded-lg">
                              <div className="text-xs text-slate-500 uppercase tracking-wider">Survival Rate</div>
                              <div className="text-2xl font-bold font-mono text-slate-700">{((stats?.detected/stats?.total)*100).toFixed(1)}%</div>
                           </div>
                        </>
                     )}
                     
                     <div className="bg-slate-50 p-4 rounded-lg">
                        <div className="text-xs text-slate-500 uppercase tracking-wider">Physics Mode</div>
                        <div className="text-lg font-bold text-slate-600 capitalize">{experimentType}</div>
                     </div>
                  </div>
               </div>
            )}

            {activeTab === 'data' && (
               <div className="overflow-x-auto border rounded-lg max-h-[400px]">
                  <table className="w-full text-sm text-left">
                     <thead className="bg-slate-50 text-slate-500 font-medium sticky top-0">
                        <tr>
                           <th className="px-4 py-3">ID</th>
                           <th className="px-4 py-3">Momentum</th>
                           <th className="px-4 py-3">Theta (mrad)</th>
                           <th className="px-4 py-3">Detected</th>
                           <th className="px-4 py-3">Material</th>
                        </tr>
                     </thead>
                     <tbody className="divide-y divide-slate-100">
                        {events.slice(0, 100).map(e => (
                           <tr key={e.id} className="hover:bg-slate-50">
                              <td className="px-4 py-2 font-mono">{e.id}</td>
                              <td className="px-4 py-2 font-mono">{e.momentum?.toFixed(3)}</td>
                              <td className="px-4 py-2 font-mono">{e.theta?.toFixed(3)}</td>
                              <td className="px-4 py-2">{e.detected ? 'Yes' : 'No'}</td>
                              <td className="px-4 py-2">{e.material}</td>
                           </tr>
                        ))}
                     </tbody>
                  </table>
                  {events.length > 100 && <div className="p-2 text-center text-xs text-slate-500">Showing first 100 events</div>}
               </div>
            )}
            
            {activeTab === 'notes' && (
                <div className="h-full">
                    <textarea 
                        className="w-full h-[350px] p-4 border border-slate-300 rounded-lg font-mono text-sm"
                        placeholder="Simulation notes..." 
                        defaultValue={`Simulation: ${experimentType.toUpperCase()}
Date: ${new Date().toLocaleString()}
Particles: ${stats?.total}`}
                    />
                </div>
            )}
         </div>
      </Modal>
   );
};

export default ResultsModal;
